import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, TrendingUp, TrendingDown, Target } from 'lucide-react';
import type { Type2SystemStats } from '@/services/conditional-skip-stats-service';

interface SystemOverviewStatsProps {
  stats: Type2SystemStats;
}

export const SystemOverviewStats: React.FC<SystemOverviewStatsProps> = ({ stats }) => {
  return (
    <div className="space-y-4">
      {/* Row 1: General stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Bots */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Type 2 Bots</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_bots}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.active_bots} active
            </p>
          </CardContent>
        </Card>

        {/* Forward Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Forward Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {stats.forward_rate > 0 ? stats.forward_rate.toFixed(1) : '0.0'}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.forwarded_signals} / {stats.total_signals} signals
            </p>
          </CardContent>
        </Card>

        {/* Skip Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Skip Rate</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-secondary">
              {stats.skip_rate > 0 ? stats.skip_rate.toFixed(1) : '0.0'}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.skipped_signals} / {stats.total_signals} signals
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Win Rate Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Win Rate (All Signals) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Win Rate (All Signals)</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">
              {stats.all_signals_win_rate > 0 ? stats.all_signals_win_rate.toFixed(1) : '0.0'}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.all_signals_wins}W / {stats.all_signals_losses}L ({stats.all_signals_trades} trades)
            </p>
            <p className="text-xs text-muted-foreground mt-1 italic">
              Before Type 2 filter
            </p>
          </CardContent>
        </Card>

        {/* Win Rate (Filtered) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Win Rate (Filtered)</CardTitle>
            <Target className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            {stats.total_trades > 0 ? (
              <>
                <div className="text-2xl font-bold text-primary">
                  {stats.system_win_rate.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.wins}W / {stats.losses}L ({stats.total_trades} trades)
                </p>
                <p className="text-xs text-muted-foreground mt-1 italic">
                  After Type 2 filter
                </p>
              </>
            ) : (
              <>
                <div className="text-lg font-semibold text-orange-500">
                  No Signal Forward
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  All signals skipped or pending
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Skip Effectiveness */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Skip Effectiveness</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              stats.win_rate_improvement_pts > 5 ? 'text-green-600' :
              stats.win_rate_improvement_pts < -5 ? 'text-red-600' :
              'text-yellow-600'
            }`}>
              {stats.win_rate_improvement_pts > 0 ? '+' : ''}
              {stats.win_rate_improvement_pts.toFixed(1)} pts
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.win_rate_improvement_pct > 0 ? '+' : ''}
              {stats.win_rate_improvement_pct.toFixed(1)}% relative
            </p>
            <p className="text-xs mt-1">
              {stats.win_rate_improvement_pts > 5 && '✅ Highly effective'}
              {stats.win_rate_improvement_pts >= -5 && stats.win_rate_improvement_pts <= 5 && '⚠️ Neutral impact'}
              {stats.win_rate_improvement_pts < -5 && '❌ Negative impact'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
